import {
  Avatar,
  AvatarGroup,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EditIcon from '@mui/icons-material/Edit';
import CubeIcon from '@mui/icons-material/ViewInAr';
import FolderIcon from '@mui/icons-material/Folder';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { GlassPanel } from '../../shared/components/GlassPanel';
import { IconTile } from '../../shared/components/IconTile';
import { Pill } from '../../shared/components/Pill';
import { EditTenantDialog } from '../../shared/components/EditTenantDialog';
import { SharedProjectChip } from '../../shared/components/SharedProjectChip';
import { useProjectSelection } from '../../shared/hooks/useProjectSelection';
import { resources } from '../../core/services/resources';
import { useTenant } from '../../core/contexts/TenantContext';
import type { AccessibleProject, Project } from '../../core/types/data-models';

// ── Mock project-detail helpers ───────────────────────────────────────────────

function mockTasks(id: number) {
  return ((id * 3 + 2) % 12) + 1;
}

// ── Project card ──────────────────────────────────────────────────────────────

function ProjectCard({
  project,
  onNavigate,
}: {
  project: Project | AccessibleProject;
  onNavigate: () => void;
}) {
  const openTasks = mockTasks(project.id);
  const shared = 'access' in project && project.access === 'shared' ? project : null;

  const docsQuery = useQuery({
    queryKey: ['documents', { projectId: project.id, recent: true }],
    queryFn: () =>
      resources.documents.list({ pageSize: 3, filter: { projectId: project.id }, sort: '-updatedAt' }),
  });
  const recentDocs = docsQuery.data?.data ?? [];
  const docsTotal = docsQuery.data?.page.total ?? 0;

  const membersQuery = useQuery({
    queryKey: ['project-members', project.id],
    queryFn: () => resources.projectMembersFor(project.id).list({ pageSize: 200 }),
  });
  const activeMembers = (membersQuery.data?.data ?? []).filter((m) => m.status !== 'REVOKED');

  return (
    <GlassPanel
      hover
      role="button"
      tabIndex={0}
      onClick={onNavigate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNavigate();
        }
      }}
      sx={{
        p: 0,
        minWidth: 300,
        flex: '1 1 300px',
        maxWidth: 420,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: 'pointer',
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {project.name}
          </Typography>
          {project.description ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {project.description}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
              No description
            </Typography>
          )}
          {shared && (
            <Box sx={{ mt: 1 }}>
              <SharedProjectChip
                tenantName={shared.tenantName}
                role={shared.role}
                sharedBy={shared.sharedBy}
              />
            </Box>
          )}
        </Box>
        <ArrowForwardIosIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0, mt: '2px' }} />
      </Box>

      <Divider />

      {/* Details */}
      <Stack spacing={2} sx={{ px: 2.5, py: 2 }}>
        {/* Artifacts */}
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <DescriptionOutlinedIcon
            fontSize="small"
            sx={{ color: 'text.secondary', mt: '2px', flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Artifacts
              </Typography>
              {docsQuery.isLoading ? (
                <CircularProgress size={12} />
              ) : (
                <Pill color="neutral">{docsTotal}</Pill>
              )}
            </Stack>
            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
              {recentDocs.map((d) => (
                <Typography
                  key={d.id}
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'block' }}
                >
                  {d.name}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Stack>

        {/* Open tasks */}
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckBoxOutlinedIcon
            fontSize="small"
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          />
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Open tasks
          </Typography>
          <Pill color={openTasks > 8 ? 'warning' : 'neutral'}>{openTasks}</Pill>
        </Stack>

        {/* Shared with */}
        <Stack direction="row" spacing={1} alignItems="center">
          {membersQuery.isLoading ? (
            <CircularProgress size={14} />
          ) : (
            <>
              <AvatarGroup
                max={3}
                sx={{
                  '& .MuiAvatar-root': {
                    width: 26,
                    height: 26,
                    fontSize: 11,
                    border: '2px solid',
                    borderColor: 'background.paper',
                  },
                }}
              >
                {activeMembers.map((m) => {
                  const label = m.name ?? m.invitedName ?? m.email;
                  return (
                    <Tooltip key={m.id} title={label}>
                      <Avatar
                        src={m.avatarUrl ?? undefined}
                        sx={{ bgcolor: 'primary.main', width: 26, height: 26, fontSize: 11 }}
                      >
                        {label[0]?.toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  );
                })}
              </AvatarGroup>
              <Typography variant="caption" color="text.secondary">
                {activeMembers.length} {activeMembers.length === 1 ? 'person' : 'people'}
              </Typography>
            </>
          )}
        </Stack>
      </Stack>
    </GlassPanel>
  );
}

function ProjectCardSkeleton() {
  return (
    <GlassPanel sx={{ p: 2.5, minWidth: 300, flex: '1 1 300px', maxWidth: 420 }}>
      <Skeleton width="60%" height={24} />
      <Skeleton width="90%" height={18} sx={{ mt: 0.5 }} />
      <Skeleton width="75%" height={18} />
      <Skeleton width="40%" height={32} sx={{ mt: 2 }} />
      <Skeleton width="30%" height={32} sx={{ mt: 1 }} />
      <Skeleton width="50%" height={32} sx={{ mt: 1 }} />
    </GlassPanel>
  );
}

function NewProjectCard({ onClick }: { onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        minWidth: 300,
        flex: '1 1 300px',
        maxWidth: 420,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        p: 4,
        borderRadius: 'var(--radius-xl)',
        cursor: 'pointer',
        border: '2px dashed',
        borderColor: 'divider',
        backgroundColor: 'transparent',
        transition: 'border-color var(--transition), background-color var(--transition)',
        '&:hover': {
          borderColor: 'var(--color-primary)',
          backgroundColor: 'var(--color-primary-soft)',
        },
      }}
    >
      <AddCircleOutlineIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        New project
      </Typography>
    </Box>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { activeTenantName, activeTenantRole } = useTenant();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    own: ownProjects,
    shared: sharedProjects,
    isLoading: myProjectsLoading,
    select: selectProject,
  } = useProjectSelection();
  const [editTenantOpen, setEditTenantOpen] = useState(false);

  // A user with access to exactly one project has nothing useful to pick
  // from here — skip straight to it, same as clicking its card would. The
  // ref guards against firing twice while the redirect's own navigate is
  // still in flight (selectProject can await a cross-tenant switch).
  const redirectedToOnlyProject = useRef(false);
  useEffect(() => {
    if (myProjectsLoading || redirectedToOnlyProject.current) return;
    if (ownProjects.length + sharedProjects.length !== 1) return;
    const onlyProject = ownProjects[0] ?? sharedProjects[0];
    redirectedToOnlyProject.current = true;
    void selectProject(onlyProject).then((next) => navigate(`/projects/${next.id}`, { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myProjectsLoading, ownProjects, sharedProjects]);

  const myTenantQuery = useQuery({
    queryKey: ['my-tenant'],
    queryFn: () => resources.myTenant.get(),
  });

  const tenantName = myTenantQuery.data?.name ?? activeTenantName;

  // Avoid flashing the project grid for the one-project redirect above.
  if (!myProjectsLoading && ownProjects.length + sharedProjects.length === 1) {
    return (
      <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 3 }}>
      {/* ── Hero ── */}
      <GlassPanel sx={{ p: { xs: 2.5, md: 3 }, position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            top: -96,
            right: -96,
            width: 384,
            height: 384,
            borderRadius: '50%',
            background: 'var(--color-primary-soft)',
            filter: 'blur(64px)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -96,
            left: -96,
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: 'var(--color-accent-cyan-soft)',
            filter: 'blur(64px)',
            pointerEvents: 'none',
          }}
        />
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          sx={{ position: 'relative' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <IconTile icon={<CubeIcon fontSize="inherit" />} color="primary" size="md" />
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h5" fontWeight={800} letterSpacing="-0.01em">
                  Dashboard
                </Typography>
                {activeTenantRole && (
                  <Pill color={activeTenantRole === 'owner' ? 'primary' : 'neutral'} mono={false}>
                    {activeTenantRole.toUpperCase()}
                  </Pill>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {tenantName
                  ? `Overview for tenant "${tenantName}".`
                  : 'Overview for the active tenant.'}
              </Typography>
            </Box>
          </Stack>
          {activeTenantRole === 'owner' && (
            <Tooltip title="Edit workspace name">
              <IconButton size="small" onClick={() => setEditTenantOpen(true)} sx={{ flexShrink: 0 }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </GlassPanel>

      {/* Project cards */}
      <Box>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
          <IconTile icon={<FolderIcon fontSize="inherit" />} color="secondary" size="sm" />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
              Projects
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {myProjectsLoading
                ? 'Loading…'
                : `${ownProjects.length} project${ownProjects.length === 1 ? '' : 's'} in this workspace`}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {myProjectsLoading
            ? Array.from({ length: 4 }, (_, i) => <ProjectCardSkeleton key={i} />)
            : (
              <>
                {ownProjects.map((p) => (
                  <ProjectCard
                    key={`${p.tenantId}-${p.id}`}
                    project={p}
                    onNavigate={() => {
                      void selectProject(p).then((next) => navigate(`/projects/${next.id}`));
                    }}
                  />
                ))}
                <NewProjectCard onClick={() => navigate('/onboarding')} />
              </>
            )
          }
        </Stack>
      </Box>

      {/* Shared-with-me cards — cross-workspace, so they show up right after an
          invitation is accepted even though the active tenant hasn't changed. */}
      {!myProjectsLoading && sharedProjects.length > 0 && (
        <Box>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
            <IconTile icon={<PeopleAltIcon fontSize="inherit" />} color="secondary" size="sm" />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
                Shared with me
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {`${sharedProjects.length} project${sharedProjects.length === 1 ? '' : 's'} shared with you`}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {sharedProjects.map((p) => (
              <ProjectCard
                key={`${p.tenantId}-${p.id}`}
                project={p}
                onNavigate={() => {
                  void selectProject(p).then((next) => navigate(`/projects/${next.id}`));
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <EditTenantDialog
        open={editTenantOpen}
        onClose={() => setEditTenantOpen(false)}
        tenant={myTenantQuery.data ?? null}
        loading={myTenantQuery.isLoading}
        onUpdated={() => queryClient.invalidateQueries({ queryKey: ['my-tenant'] })}
      />
    </Box>
  );
}
